<template>
  <v-card class="pa-4">
    <v-card-title class="text-h5 mb-4">
      {{ title }}
    </v-card-title>

    <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit">
      <v-row>
        <v-col cols="12" md="6">
          <v-select
            v-model="formData.group_id"
            :items="groups"
            item-title="name"
            item-value="id"
            label="Grupo"
            :rules="groupRules"
            required
            variant="outlined"
            :loading="groupsLoading"
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="formData.school_subject_id"
            :items="schoolSubjects"
            item-title="name"
            item-value="id"
            label="Asignatura"
            variant="outlined"
            :loading="schoolSubjectsLoading"
            hint="Seleccione la asignatura para este PIN"
            persistent-hint
            :rules="subjectRules"
            required
            no-data-text="No se encontraron asignaturas"
          >
            <template v-slot:prepend-inner>
              <v-icon>mdi-book-open-variant</v-icon>
            </template>
          </v-autocomplete>
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="formData.code_length"
            label="Longitud del código"
            type="number"
            :rules="codeLengthRules"
            min="4"
            max="12"
            variant="outlined"
            hint="Entre 4 y 12 dígitos"
            persistent-hint
            disabled
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="formData.expires_at"
            label="Fecha de expiración"
            type="datetime-local"
            :rules="expirationRules"
            variant="outlined"
            required
          />
        </v-col>

        <v-col cols="12" md="6">
          <v-text-field
            v-model="formData.extra_time"
            label="Tiempo extra (segundos)"
            type="number"
            :rules="extraTimeRules"
            min="0"
            variant="outlined"
            hint="Tiempo adicional en segundos (opcional)"
            persistent-hint
          />
        </v-col>

        <!-- Selector de pregunta específica -->
        <v-col cols="12">
          <QuestionSelector
            v-model="formData.user_selected_question_id"
            :school-grade-id="selectedSchoolGradeId"
            :school-subject-id="formData.school_subject_id"
            @question-selected="handleQuestionSelected"
          />
        </v-col>
      </v-row>

      <v-alert v-if="error" type="error" class="mb-4" closable @click:close="clearError">
        {{ error }}
      </v-alert>

      <v-alert v-if="generatedPin" type="success" class="mb-4">
        <div class="text-h6 mb-2">¡PIN generado exitosamente!</div>
        <div class="text-h4 text-center py-3 bg-green-lighten-5 rounded">
          {{ generatedPin.code }}
        </div>
        <div class="mt-2">
          <strong>Grupo:</strong> {{ generatedPin.group?.name || generatedPin.group_name || 'N/A'
          }}<br />
          <strong>Asignatura:</strong> {{ generatedPin.school_subject?.name || 'Sin asignatura' }}<br />
          <strong>Expira:</strong> {{ formatDate(generatedPin.expires_at) }}<br />
          <strong>Tiempo extra:</strong> {{ generatedPin.extra_time || 0 }} segundos
        </div>
      </v-alert>

      <v-card-actions>
        <v-spacer />
        <v-btn color="primary" type="submit" :loading="loading" :disabled="!valid" size="large">
          Generar PIN
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import QuestionSelector from '@/components/questions/QuestionSelector.vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Generar PIN',
  },
})

const emit = defineEmits(['pin-generated'])

const store = useStore()

const form = ref()
const valid = ref(false)
const generatedPin = ref(null)
const selectedQuestionData = ref(null)

const formData = reactive({
  group_id: null,
  school_subject_id: null,
  code_length: 6,
  expires_at: '',
  extra_time: 0,
  user_selected_question_id: null,
})

const loading = computed(() => store.getters['pins/isLoading'])
const error = computed(() => store.getters['pins/getError'])
const groups = computed(() => store.getters['groups/allGroups'] || [])
const groupsLoading = computed(() => store.getters['groups/isLoading'])
const schoolSubjects = computed(() => store.getters['schoolSubjects/allSubjects'] || [])
const schoolSubjectsLoading = computed(() => store.getters['schoolSubjects/isLoading'])

// Obtener school_grade_id del grupo seleccionado
const selectedSchoolGradeId = computed(() => {
  if (!formData.group_id) return null
  const selectedGroup = groups.value.find(g => g.id === formData.group_id)
  return selectedGroup?.school_grade_id || null
})

// Validation rules
const groupRules = [(v) => !!v || 'Seleccione un grupo']

const subjectRules = [(v) => !!v || 'Seleccione una asignatura']

const codeLengthRules = [
  (v) => !!v || 'La longitud del código es requerida',
  (v) => (v >= 4 && v <= 12) || 'La longitud debe estar entre 4 y 12 dígitos',
]

const expirationRules = [
  (v) => !!v || 'La fecha de expiración es requerida',
  (v) => new Date(v) > new Date() || 'La fecha debe ser futura',
]

const extraTimeRules = [
  (v) => v === null || v === '' || v >= 0 || 'El tiempo extra no puede ser negativo',
]

const handleQuestionSelected = (question) => {
  selectedQuestionData.value = question
  console.log('Pregunta seleccionada:', question)
}

const handleSubmit = async () => {
  if (!valid.value) return

  const pinData = {
    group_id: formData.group_id,
    school_subject_id: formData.school_subject_id,
    code_length: parseInt(formData.code_length),
    expires_at: new Date(formData.expires_at).toISOString(),
    extra_time: parseInt(formData.extra_time) || 0,
    user_selected_question_id: formData.user_selected_question_id || null,
  }

  const result = await store.dispatch('pins/generatePin', pinData)

  if (result.success) {
    generatedPin.value = result.data
    emit('pin-generated', result.data)
    resetForm()
  }
}

const resetForm = () => {
  form.value?.reset()
  formData.group_id = null
  formData.school_subject_id = null
  formData.code_length = 6
  formData.expires_at = ''
  formData.extra_time = 0
  formData.user_selected_question_id = null
  selectedQuestionData.value = null
}

const clearError = () => {
  store.dispatch('pins/clearError')
  generatedPin.value = null
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Set default expiration time (1 hour from now)
const setDefaultExpiration = () => {
  const now = new Date()
  now.setHours(now.getHours() + 1)
  formData.expires_at = now.toISOString().slice(0, 16)
}

onMounted(async () => {
  setDefaultExpiration()

  // Load groups and school subjects
  await Promise.all([
    store.dispatch('groups/fetchGroups'),
    store.dispatch('schoolSubjects/fetchAllSubjects')
  ])
})
</script>

<style scoped>
.v-alert .text-h4 {
  font-family: 'Courier New', monospace;
  font-weight: bold;
  letter-spacing: 2px;
}
</style>
